import { useEffect } from 'react';

/** field name -> message, as routed by utils/serverStepErrors for this step. */
export type ServerFieldErrors = Record<string, string> | undefined;

type SetError = (name: any, error: { type: string; message: string }) => void;

/**
 * Replays the server's keyed validation errors onto a step's own form.
 *
 * MUI's StepContent is unmountOnExit, so an inactive step has no form instance and
 * the container cannot call its setError. The container keeps the errors instead and
 * passes them down; this runs when the step mounts (i.e. once the container has
 * navigated to it) and attaches each message to its field.
 *
 * Must be called AFTER useHookForm so that hook's own defaultValues effect has
 * already populated the fields — setting values afterwards would not clear these
 * errors, but ordering keeps the intent obvious rather than incidental.
 */
export const useServerErrors = (setError: SetError, serverErrors: ServerFieldErrors) => {
  useEffect(() => {
    if (!serverErrors) {
      return;
    }
    Object.keys(serverErrors).forEach((field) => {
      setError(field, { type: 'server', message: serverErrors[field] });
    });
    // setError is stable for a given form instance; re-running on serverErrors
    // identity is what re-surfaces the errors after a second failed submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverErrors]);
};

export default useServerErrors;
